/**
 * Bulk learner upload (Figma 9178:242666 light / 9178:242701 dark).
 *
 * Flow: download the CSV template → fill it → drop/browse it here →
 * rows are parsed + validated client-side → Bulk Upload posts the valid
 * rows to POST /learners/bulk. Nothing is sent until the trainer confirms.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import UploadFileIcon from '@/components/icons/UploadFileIcon';
import { useBulkImportLearners } from '../../services/learners';
import {
  checkFile,
  downloadTemplate,
  parseLearnersCsv,
  type ParsedFile,
} from '@/utils/learnerCsv';

const LearnerBulkUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const bulkImport = useBulkImportLearners();

  const takeFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    const problem = checkFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const result = await parseLearnersCsv(file);
      setParsed(result);
      if (result.valid.length === 0) {
        toast.error('No valid rows found — check the column names match the template.');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the file.');
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      void takeFile(e.dataTransfer.files[0]);
    },
    [takeFile]
  );

  const submit = async () => {
    if (!parsed || parsed.valid.length === 0 || bulkImport.isPending) return;
    try {
      const result = await bulkImport.mutateAsync({ learners: parsed.valid });
      if (result.failed > 0) {
        toast.success(
          `${result.successful} learner${result.successful === 1 ? '' : 's'} added — ${result.failed} skipped (already exist or invalid).`
        );
      } else {
        toast.success(`${result.successful} learner${result.successful === 1 ? '' : 's'} added.`);
      }
      navigate('/learners');
    } catch {
      toast.error('The upload failed. Nothing was saved — please try again.');
    }
  };

  const canSubmit = !!parsed && parsed.valid.length > 0 && !bulkImport.isPending;

  return (
    <motion.div {...slideUp} className="space-y-8 p-4 md:p-6">
      {/* Header — same pattern as the single add-learner page */}
      <PageHeader title="Add Learners in Bulk" trail={[{ label: 'Learners', to: '/learners' }]} />

      <div className="flex flex-col items-center gap-6 max-w-[904px] mx-auto">
        {/* Step 1 — the template */}
        <button
          type="button"
          onClick={downloadTemplate}
          className="inline-flex items-center justify-center gap-2 min-h-[36px] w-[240px] px-3 py-1.5 rounded-full border border-border-primary bg-surface-elevated text-sm font-bold text-text-primary hover:border-primary-500/60 focus-ring transition-colors duration-fast"
        >
          Download the CSV form
          <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
        </button>

        {/* Step 2 — the dropzone */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Drop a CSV file here or browse"
          data-testid="bulk-dropzone"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
          onDrop={onDrop}
          className={`w-full flex flex-col items-center justify-center gap-2 min-h-[308px] p-10 rounded-2xl cursor-pointer
            border-[3px] border-dashed bg-gradient-to-r from-primary-500/[0.08] to-secondary-500/[0.08]
            focus-ring transition-colors duration-fast
            ${isDragging ? 'border-secondary-500' : 'border-primary-500'}`}
        >
          <UploadFileIcon className="w-[120px] h-[120px]" />
          <p className="text-lg font-semibold text-text-primary">Drop or select file</p>
          <p className="body-small text-text-secondary text-center">
            Drop the filled-out CSV here or click to{' '}
            <span className="text-primary-600 dark:text-primary-350 font-medium">browse</span>{' '}
            through your machine.
          </p>

          {parsed && (
            <div
              className="mt-4 w-full max-w-[560px] flex items-center gap-3 rounded-xl border border-border-primary bg-surface-elevated px-4 py-3"
              onClick={(e) => e.stopPropagation()}
            >
              <DocumentTextIcon className="w-6 h-6 text-primary-600 dark:text-primary-350 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-semibold text-text-primary truncate">{parsed.name}</p>
                <p className="text-xs text-text-secondary">
                  {parsed.valid.length} ready to add
                  {parsed.errors.length > 0 && ` · ${parsed.errors.length} row${parsed.errors.length === 1 ? '' : 's'} skipped`}
                </p>
              </div>
              <button
                type="button"
                aria-label="Remove file"
                onClick={() => setParsed(null)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-secondary hover:bg-text-tertiary/10 transition-colors duration-fast"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="flex items-start justify-center gap-3 pt-5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center justify-center min-h-[36px] px-3 py-1.5 rounded-full border border-border-primary bg-surface-elevated text-sm font-bold text-text-primary hover:border-primary-500/60 focus-ring transition-colors duration-fast"
            >
              Upload from computer
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 min-h-[36px] min-w-[114px] px-3 py-1.5 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 text-sm font-bold hover:opacity-90 focus-ring disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-fast"
            >
              <ArrowUpTrayIcon className="w-5 h-5" aria-hidden="true" />
              Add
            </button>
          </div>
        </div>

        {/* Skipped-row details so nobody wonders where a learner went */}
        {parsed && parsed.errors.length > 0 && (
          <div className="w-full rounded-xl border border-border-primary bg-surface-elevated p-4" data-testid="bulk-errors">
            <p className="text-sm font-semibold text-text-primary mb-2">
              Rows that will be skipped
            </p>
            <ul className="space-y-1">
              {parsed.errors.slice(0, 8).map((e) => (
                <li key={`${e.row}-${e.error}`} className="text-sm text-text-secondary">
                  Row {e.row}{e.email && ` (${e.email})`}: {e.error}
                </li>
              ))}
              {parsed.errors.length > 8 && (
                <li className="text-sm text-text-tertiary">…and {parsed.errors.length - 8} more</li>
              )}
            </ul>
          </div>
        )}

        {/* Step 3 — the confirm */}
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center justify-center min-h-[36px] w-[150px] px-3 py-1.5 rounded-full bg-grey-900 hover:bg-grey-800 dark:bg-grey-800 dark:hover:bg-grey-700 text-sm font-bold text-white focus-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-fast"
        >
          {bulkImport.isPending ? 'Uploading…' : 'Bulk Upload'}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        aria-label="Choose a CSV file"
        onChange={(e) => {
          void takeFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </motion.div>
  );
};

export default LearnerBulkUploadPage;
